import { useEffect, useState } from 'react'
import '../css/pages/settings.css'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components' 
import NotifFeed from 'components/notifications/NotifFeed'
import Sidebar from 'components/Sidebar'
import { PageContainerMain } from 'pages/pages.styled'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep'
import { deleteAllNotifications, markAllNotificationsRead } from 'features/notifs/notifSlice'
import { STATUS } from 'features/enums'
import { useTranslation } from 'react-i18next'

export default function Notifications() {
  const navigate = useNavigate()
  const { t } = useTranslation('notifications')
  const dispatch = useDispatch()
  const [viewActions, setViewActions] = useState(false)
  const { user} = useSelector((state) => state.auth)
  const { allNotifications, bulkActionStatus, unreadCount } = useSelector((state) => state.notifications)
  const hasNotifications = allNotifications.length > 0
  const isBulkLoading = bulkActionStatus === STATUS.LOADING
  
  useEffect(() => {
    if(!user){navigate('/login')}
    
  }, [user, navigate])

  const handleMarkAllRead = () => {
    setViewActions(false)
    dispatch(markAllNotificationsRead())
  }

  const handleDeleteAll = () => {
    setViewActions(false)
    dispatch(deleteAllNotifications())
  }
  
  return ( 
    <PageContainerMain> 
      <Sidebar />  
      <NotificationsElementsContainer> 
        <NotificationsHeader>
          <HeaderTextWrap>
            <NotificationsTitle>{t('title')}</NotificationsTitle>
            <NotificationsSubtitle>
              {hasNotifications
                ? t(unreadCount > 0 ? 'summary.updatesWithUnread' : 'summary.updates', { count: allNotifications.length, unreadCount })
                : t('summary.caughtUp')}
            </NotificationsSubtitle>
          </HeaderTextWrap>

          <HeaderActions>
            <ActionButton
              type="button"
              disabled={!hasNotifications || isBulkLoading}
              onClick={() => setViewActions((prevState) => !prevState)}
              aria-label={t('accessibility.actions')}
            >
              <MoreVertIcon />
            </ActionButton>
            {viewActions && (
              <ActionMenu>
                <ActionMenuItem
                  type="button"
                  disabled={isBulkLoading}
                  onClick={handleMarkAllRead}
                >
                  <DoneAllIcon />
                  <span>{t('markAllRead')}</span>
                </ActionMenuItem>
                <ActionMenuItem
                  type="button"
                  disabled={isBulkLoading}
                  onClick={handleDeleteAll}
                  $danger
                >
                  <DeleteSweepIcon />
                  <span>{t('deleteAll')}</span>
                </ActionMenuItem>
              </ActionMenu>
            )}
          </HeaderActions>
        </NotificationsHeader>
        <NotifFeed />
      </NotificationsElementsContainer>
    </PageContainerMain>
  )
}

  
const NotificationsElementsContainer = styled.div`
  width: min(720px, 100%);
  display: flex; 
  height: 100%;
  flex-direction: column;
  align-items: stretch;
  color: var(--lightThemeFontColor);
  background: #fafafa;
  border: none;
  border-radius: 25px;
  overflow: hidden;
  @media (max-width: 768px) {
    width: 100%;
    border-right: none;
    border-left: none;
    border-radius: 25px;
  }
`

const NotificationsHeader = styled.div`
  width: 100%;
  min-height: 86px;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: #fff;
  border-bottom: 1px solid var(--borderColor);
`

const HeaderTextWrap = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
`

const NotificationsTitle = styled.h1`
  margin: 0;
  font-family: 'Roboto', sans-serif;
  font-size: 24px;
  font-weight: 600;
  color: var(--lightThemeFontColor);
`

const NotificationsSubtitle = styled.p`
  margin: 0;
  font-family: 'Roboto', sans-serif;
  font-size: 13px;
  color: var(--dFontColor);
`

const HeaderActions = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`

const ActionButton = styled.button`
  width: 40px;
  height: 40px;
  border: 1px solid var(--borderColor);
  border-radius: 8px;
  background: #fff;
  color: var(--lightThemeFontColor);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: var(--themeColor);
    color: var(--themeColor);
    background: rgba(0, 180, 204, 0.08);
  }

  &:disabled {
    cursor: default;
    opacity: 0.45;
  }
`

const ActionMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 2;
  width: 190px;
  padding: 6px;
  border: 1px solid var(--borderColor);
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.12);
`

const ActionMenuItem = styled.button`
  width: 100%;
  height: 38px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: ${props => props.$danger ? '#b42318' : 'var(--lightThemeFontColor)'};
  cursor: pointer;
  display: grid;
  grid-template-columns: 22px 1fr;
  align-items: center;
  gap: 8px;
  padding: 0 9px;
  font-family: 'Roboto', sans-serif;
  font-size: 13px;
  text-align: left;

  svg {
    font-size: 19px;
  }

  &:hover:not(:disabled) {
    background: ${props => props.$danger ? 'rgba(180, 35, 24, 0.08)' : 'rgba(0, 180, 204, 0.08)'};
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`
